import { clamp } from './controls';

/*
 * The Pro drawer's capture modes (2026-10-22; docs/FIELD_AI_VISION_SPEC.md
 * 10.2): what one press of the shutter takes. Pure, no DOM: the frames are
 * taken and scored by whoever calls (utils/fieldFrame.js on a real camera, a
 * test's stand-ins here).
 *
 *   single    one frame, as always.
 *   burst     BURST frames BURST.intervalMs apart; the sharpest is kept (the
 *             focus meter's sharpness, utils/spores/focus.js), the rest let go.
 *   bracket   one frame at each of BRACKET_EV stops: -1, 0 and +1 EV from
 *             the exposure set now. Offered only where the camera sets its
 *             own exposure: a digital brightness would only brighten the same
 *             frame. Whatever happens, the exposure is put back.
 *   zstack    a run of frames at different focus depths, kept as one stack
 *             (section 10.3; components/zstack/useZStack.js), where the host
 *             offers it: the shutter starts it instead of taking a frame.
 *
 * No merging: an HDR or a focus stack is not made here (v1). Each host keeps
 * what a mode took as its own media: Field Mode files a bracket's three as
 * photos of the specimen, the dish and microscope cameras let the person pick
 * one. The mode lasts while the camera is open, never stored.
 */
export const MODES = Object.freeze(['single', 'burst', 'bracket', 'zstack']);
export const BURST = Object.freeze({ intervalMs: 150, frames: Object.freeze([3, 5]) });
export const BRACKET_EV = Object.freeze([-1, 0, 1]);
//: how long the camera is given to take a new exposure before its frame is grabbed
export const BRACKET_SETTLE_MS = 350;

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

/** The modes this camera can do: a bracket needs the camera's own exposure; a Z-stack, a host that takes one
 *  (by hand where the camera cannot step its focus). */
export function modesFor(caps, { zstack = false } = {}) {
  if (!caps) return ['single'];
  const bracket = caps.exposure?.kind === 'camera' || !!caps.exposureLock;
  return MODES.filter(mode => (mode !== 'bracket' || bracket) && (mode !== 'zstack' || zstack));
}

/**
 * The constraint that takes a bracket's `ev` stop from the exposure set now:
 * by the shutter time where the exposure is set by hand (`manual`, x2 a
 * stop), else by the exposure compensation; clamped to what the camera
 * allows. Null where neither can move.
 */
export function bracketConstraints(ev, { manual = null, exposure = 0 } = {}, caps = {}) {
  if (manual && caps.exposureTime && Number.isFinite(manual.exposureTime)) {
    const exposureTime = Math.round(clamp(manual.exposureTime * 2 ** ev, caps.exposureTime) * 1000) / 1000;
    return { advanced: [{ exposureMode: 'manual', exposureTime,
                          ...(caps.iso && Number.isFinite(manual.iso) ? { iso: manual.iso } : {}) }] };
  }
  if (caps.exposure?.kind === 'camera') {
    const value = Math.round(clamp(exposure + ev, caps.exposure) * 1000) / 1000;
    return { advanced: [{ ...(caps.exposureMode ? { exposureMode: caps.exposureMode } : {}), exposureCompensation: value }] };
  }
  return null;
}

/**
 * A burst: `frames` frames `intervalMs` apart, each scored as it is taken,
 * only the best kept (a frame with `release()` is told when it is let go).
 * {frame, index, scores}.
 */
export async function captureBurst({ frames = BURST.frames[0], intervalMs = BURST.intervalMs, take, score, sleep = wait }) {
  let best = null;
  const scores = [];
  for (let i = 0; i < frames; i += 1) {
    if (i) await sleep(intervalMs);
    const frame = await take();
    const value = Number(score(frame)) || 0;
    scores.push(value);
    if (!best || value > best.score) {
      best?.frame?.release?.();
      best = { frame, score: value, index: i };
    } else {
      frame?.release?.();
    }
  }
  return { frame: best.frame, index: best.index, scores };
}

/** A bracket the camera would not take: nothing was kept, the exposure is back as it was. */
export class BracketRefused extends Error {
  constructor() {
    super('bracket-refused');
    this.name = 'BracketRefused';
  }
}

/**
 * A bracket: for each stop, `apply(ev)` (true once the camera took it), a
 * wait for it to settle, then `grab()`. `restore()` always runs after, taken
 * or refused. [{ev, shot}] in the order of `steps`.
 */
export async function captureBracket({ steps = BRACKET_EV, apply, restore, grab, settleMs = BRACKET_SETTLE_MS, sleep = wait }) {
  const shots = [];
  try {
    for (const ev of steps) {
      if (!(await apply(ev))) throw new BracketRefused();
      await sleep(settleMs);
      shots.push({ ev, shot: await grab() });
    }
  } finally {
    await restore();
  }
  return shots;
}

/** "−1 EV", "0 EV", "+1 EV": a stop as the bracket's labels write it (a true minus sign). */
export const evLabel = ev => `${ev > 0 ? '+' : ev < 0 ? '−' : ''}${Math.abs(ev)} EV`;
